/**
 * @fileoverview Date range picker component for selecting start and end dates
 * @module ctl-date-range-picker
 * @version 1.0.0
 * 
 * @description
 * A date range picker component that provides two date inputs for selecting
 * a start and end date range. Includes validation to ensure end date is after start date.
 * Supports a "single" mode for selecting a single date only.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-date-range-picker label="Select Date Range"></ctl-date-range-picker>
 * 
 * @example
 * <!-- With initial values -->
 * <ctl-date-range-picker 
 *   label="Trip Dates" 
 *   start="2024-06-01" 
 *   end="2024-06-15"
 * ></ctl-date-range-picker>
 * 
 * @example
 * <!-- Single date mode -->
 * <ctl-date-range-picker label="Move-in date" mode="single"></ctl-date-range-picker>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    --ctl-radius: var(--border-radius-md, 8px);

    --ctl-color-focus: var(--ctl-color-brand);
    --ctl-field-height: var(--input-height, 40px);
    --ctl-pad-x: var(--spacing-sm, 12px);
    --ctl-gap: var(--spacing-xs, 8px);

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xxs, 4px);
  }

  .label {
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: var(--font-weight-normal, 400);
    color: var(--ctl-color-text);
  }

  .range-container {
    display: flex;
    gap: var(--ctl-gap);
    align-items: center;
  }

  .date-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xxs, 4px);
  }

  .date-label {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: var(--ctl-gap);
    height: var(--ctl-field-height);
    padding: 0 var(--ctl-pad-x);
    border-radius: var(--ctl-radius);
    border: var(--border-width-thin, 1px) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    box-sizing: border-box;
    transition: border-color var(--transition-fast, 150ms ease-in-out), box-shadow var(--transition-fast, 150ms ease-in-out), background var(--transition-fast, 150ms ease-in-out), opacity var(--transition-fast, 150ms ease-in-out);
  }

  :host([invalid]) .input-wrapper {
    border-color: var(--ctl-color-danger);
  }

  .input-wrapper:focus-within {
    border-color: var(--ctl-color-focus);
    box-shadow: var(--ctl-shadow-sm);
  }

  :host([disabled]) .input-wrapper {
    opacity: var(--opacity-60, 0.6);
    background: color-mix(in srgb, var(--color-black) 2%, transparent);
    cursor: not-allowed;
  }

  input[type="date"] {
    flex: 1;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: var(--font-size-sm, 0.875rem);
    color: var(--ctl-color-text);
    outline: none;
    padding: 0;
    min-width: 0;
  }

  input[type="date"]:disabled {
    cursor: not-allowed;
  }

  .separator {
    color: var(--ctl-color-muted);
    font-size: var(--font-size-lg, 1.125rem);
    flex-shrink: 0;
  }

  /* Single date mode */
  :host([mode="single"]) .end-wrapper-col,
  :host([mode="single"]) .separator {
    display: none;
  }

  .help {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
  }

  .error {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-danger);
  }

  .error:empty {
    display: none;
  }

  @media (max-width: var(--breakpoint-sm, 640px)) {
    .range-container {
      flex-direction: column;
      align-items: stretch;
    }

    .separator {
      transform: rotate(90deg);
      align-self: center;
    }
  }
</style>

<div class="container">
  <label class="label" part="label"></label>
  <div class="range-container" part="range-container">
    <div class="date-wrapper">
      <div class="date-label">Start Date</div>
      <div class="input-wrapper start-wrapper" part="start-wrapper">
        <input type="date" class="start-input" part="start-input" />
      </div>
    </div>
    <span class="separator">→</span>
    <div class="date-wrapper end-wrapper-col">
      <div class="date-label">End Date</div>
      <div class="input-wrapper end-wrapper" part="end-wrapper">
        <input type="date" class="end-input" part="end-input" />
      </div>
    </div>
  </div>
  <div class="help" part="help"></div>
  <div class="error" part="error"></div>
</div>
`;

/**
 * Date Range Picker Web Component
 * 
 * @class CtlDateRangePicker
 * @extends HTMLElement
 * 
 * @attr {string} label - Label text for the date range picker
 * @attr {string} mode - Mode: "single" (one date input) or "range" (default)
 * @attr {string} start - Start date value in YYYY-MM-DD format
 * @attr {string} end - End date value in YYYY-MM-DD format
 * @attr {string} min - Minimum selectable date in YYYY-MM-DD format
 * @attr {string} max - Maximum selectable date in YYYY-MM-DD format
 * @attr {string} help - Help text displayed below the inputs
 * @attr {string} error - Error message displayed below the inputs
 * @attr {boolean} required - Whether the date range is required
 * @attr {boolean} disabled - Whether the date range picker is disabled
 * @attr {boolean} invalid - Whether the date range has validation errors
 * 
 * @fires ctl-change - Dispatched when either date value changes
 * @fires ctl-change.detail.start - The start date value
 * @fires ctl-change.detail.end - The end date value (range mode only; absent in single mode)
 * @fires ctl-change.detail.isValid - Whether the value is valid.
 *   In range mode: true when end date >= start date (or either is empty).
 *   In single mode: true when the start input satisfies its constraints (required, min, max).
 * 
 * @csspart label - The label element
 * @csspart range-container - The container for both date inputs
 * @csspart start-wrapper - The start date input wrapper
 * @csspart start-input - The start date input element
 * @csspart end-wrapper - The end date input wrapper
 * @csspart end-input - The end date input element
 * @csspart help - The help text element
 * @csspart error - The error message element
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color for labels
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-focus - Focus state color
 * @cssprop --ctl-color-danger - Error state color
 * @cssprop --ctl-field-height - Input field height
 * @cssprop --ctl-radius - Border radius
 */
export class CtlDateRangePicker extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._startInput = this.shadowRoot.querySelector('.start-input');
    this._endInput = this.shadowRoot.querySelector('.end-input');
    this._label = this.shadowRoot.querySelector('.label');
    this._help = this.shadowRoot.querySelector('.help');
    this._error = this.shadowRoot.querySelector('.error');
  }

  static get observedAttributes() {
    return ['label', 'mode', 'start', 'end', 'min', 'max', 'help', 'error', 
            'required', 'disabled', 'invalid'];
  }

  connectedCallback() {
    this._startInput.addEventListener('change', this._handleChange.bind(this));
    this._endInput.addEventListener('change', this._handleChange.bind(this));
    this._render();
  }

  disconnectedCallback() {
    this._startInput.removeEventListener('change', this._handleChange.bind(this));
    this._endInput.removeEventListener('change', this._handleChange.bind(this));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      // When switching to single mode, clear stale end-date state and any
      // range-specific validation errors that no longer apply.
      if (name === 'mode' && newValue === 'single') {
        this._endInput.value = '';
        this.removeAttribute('end');
        if (this.hasAttribute('invalid')) {
          this.removeAttribute('invalid');
        }
        const currentError = this.getAttribute('error');
        if (currentError === 'End date must be after start date') {
          this.removeAttribute('error');
        }
      }
      this._render();
    }
  }

  _handleChange() {
    const start = this._startInput.value;
    const end = this._endInput.value;
    const isSingle = this.getAttribute('mode') === 'single';
    
    // Validate range (only relevant in range mode)
    const isValid = isSingle
      ? this._startInput.checkValidity()
      : this._validateRange(start, end);
    
    if (!isSingle && !isValid && start && end) {
      this.setAttribute('invalid', '');
      this.setAttribute('error', 'End date must be after start date');
    } else if (this.hasAttribute('invalid') && isValid) {
      this.removeAttribute('invalid');
      if (!this.getAttribute('error') || this.getAttribute('error') === 'End date must be after start date') {
        this.removeAttribute('error');
      }
    }

    // Update attributes
    if (start) this.setAttribute('start', start);
    if (isSingle) {
      // Ensure no stale end value persists in single mode
      this._endInput.value = '';
      this.removeAttribute('end');
    } else if (end) {
      this.setAttribute('end', end);
    }

    // Dispatch event
    const detail = isSingle
      ? { start, isValid }
      : { start, end, isValid };

    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  _validateRange(start, end) {
    if (!start || !end) return true; // Allow incomplete ranges
    return new Date(end) >= new Date(start);
  }

  _render() {
    // Label
    const label = this.getAttribute('label');
    if (label) {
      this._label.textContent = label;
      this._label.style.display = 'block';
    } else {
      this._label.style.display = 'none';
    }

    // Start date
    const start = this.getAttribute('start');
    if (start !== null && this._startInput.value !== start) {
      this._startInput.value = start;
    }

    // End date
    const end = this.getAttribute('end');
    if (end !== null && this._endInput.value !== end) {
      this._endInput.value = end;
    }

    // Min/Max
    const min = this.getAttribute('min');
    const max = this.getAttribute('max');
    if (min) {
      this._startInput.min = min;
      this._endInput.min = min;
    }
    if (max) {
      this._startInput.max = max;
      this._endInput.max = max;
    }

    // Required
    const required = this.hasAttribute('required');
    this._startInput.required = required;
    this._endInput.required = required;

    // Disabled
    const disabled = this.hasAttribute('disabled');
    this._startInput.disabled = disabled;
    this._endInput.disabled = disabled;

    // Help text
    const help = this.getAttribute('help');
    if (help) {
      this._help.textContent = help;
      this._help.style.display = 'block';
    } else {
      this._help.style.display = 'none';
    }

    // Error message
    const error = this.getAttribute('error');
    if (error) {
      this._error.textContent = error;
      this._error.style.display = 'block';
    } else {
      this._error.style.display = 'none';
    }
  }

  /**
   * Get the current date range
   * @returns {{start: string, end: string}} The current date range
   */
  get value() {
    return {
      start: this._startInput.value,
      end: this._endInput.value
    };
  }

  /**
   * Set the date range
   * @param {{start: string, end: string}} range - Date range object
   */
  set value(range) {
    if (range.start) this.setAttribute('start', range.start);
    if (range.end) this.setAttribute('end', range.end);
  }

  /**
   * Check if the date range is valid
   * @returns {boolean} True if valid, false otherwise
   */
  checkValidity() {
    const start = this._startInput.value;
    const end = this._endInput.value;
    const isSingle = this.getAttribute('mode') === 'single';
    if (isSingle) {
      return this._startInput.checkValidity();
    }
    return this._startInput.checkValidity() && 
           this._endInput.checkValidity() && 
           this._validateRange(start, end);
  }

  /**
   * Reset the date range picker
   */
  reset() {
    this._startInput.value = '';
    this._endInput.value = '';
    this.removeAttribute('start');
    this.removeAttribute('end');
    this.removeAttribute('invalid');
  }
}

if (!customElements.get("ctl-date-range-picker")) {
  customElements.define("ctl-date-range-picker", CtlDateRangePicker);
}
